/**
 * AI Mock Data Generator
 * Uses Claude API to generate realistic mock data for testing
 */

/**
 * Generate mock parent insights based on student profile
 * @param {object} studentProfile - Complete student academic profile
 * @param {string} contextNotes - Optional quick context notes to inform insights
 * @returns {Promise<object>} - { parentA: object, parentB: object }
 */
export async function generateMockParentInsights(studentProfile, contextNotes = '') {
  console.log('🤖 Generating mock parent insights for:', studentProfile.name);

  const prompt = `You are helping create realistic parent observations for a test student profile.

**STUDENT PROFILE:**
Name: ${studentProfile.name}
Graduation Year: ${studentProfile.graduation_year || 'Not specified'}
Intended Major: ${studentProfile.intended_major || 'Not specified'}
GPA: ${studentProfile.gpa_weighted || studentProfile.gpa_unweighted || 'Not specified'}
${studentProfile.sat_total ? `SAT: ${studentProfile.sat_total}` : ''}
${studentProfile.act_composite ? `ACT: ${studentProfile.act_composite}` : ''}
Academic Interests: ${Array.isArray(studentProfile.academic_interests) ? studentProfile.academic_interests.join(', ') : 'Not specified'}
Career Goals: ${studentProfile.career_goals || 'Not specified'}
Activities: ${Array.isArray(studentProfile.activities) ? studentProfile.activities.map(a => a.name).join(', ') : 'Not specified'}
Special Talents: ${studentProfile.special_talents || 'Not specified'}
${contextNotes ? `\n**ADDITIONAL CONTEXT/KEY FACTS:**\n${contextNotes}\n` : ''}

**TASK:**
Generate realistic, detailed parent observations that sound authentic and insightful. Create observations for TWO parents that complement each other but offer different perspectives.

**PARENT A OBSERVATIONS** (First parent - typically more involved in academics):
1. easy_subject: What subject or activity does your child find surprisingly easy that others struggle with?
2. growth: Describe a time when you watched your child grow through a challenge or setback.
3. learning_difference: When has your child's way of thinking or learning been different from what schools typically expect?
4. surprises: What does your child do that consistently surprises adults?
5. age_limit: Has your child ever been told they couldn't do something due to age/grade level, but you suspected they could handle it?
6. rules_questioned: What rules or systems has your child questioned that made you think 'actually, they have a point'?
7. wrong_level: Has your child ever been placed in the 'wrong' level for something?
8. advocacy_success: Describe a time your child advocated for themselves or others and succeeded.
9. initiative_against_advice: When has your child taken initiative on something despite being advised against it?
10. mechanical_skills: What practical skills or mechanical abilities does your child have?
11. math_integration: When does your child bring mathematical thinking into unexpected situations?
12. entrepreneurial_instincts: When has your child shown entrepreneurial instincts?
13. obsessions: What does your child get obsessed with?
14. unusual_interests: What's something your child cares deeply about that doesn't seem typical?
15. hidden_qualities: What do you hope college admissions officers will understand about your child?

**PARENT B OBSERVATIONS** (Second parent - offers different perspective):
1. easy_subject: What subject or activity does your child find surprisingly easy?
2. growth: Describe a time when you watched your child grow through a challenge.
3. learning_difference: When has your child's way of thinking been different?
4. surprises: What does your child do that consistently surprises adults?
5. age_limit: Has your child ever been told they couldn't do something due to age?
6. rules_questioned: What rules or systems has your child questioned?
7. wrong_level: Has your child ever been placed in the 'wrong' level?
8. advocacy_success: Describe a time your child advocated for themselves.
9. initiative_against_advice: When has your child taken initiative despite advice?
10. mechanical_skills: What practical skills does your child have?
11. math_integration: When does your child bring mathematical thinking into situations?
12. entrepreneurial_instincts: When has your child shown entrepreneurial instincts?
13. obsessions: What does your child get obsessed with?
14. unusual_interests: What's something your child cares deeply about?
15. hidden_qualities: What should college admissions officers understand?
16. different_perspective: Where do you and your spouse see your child differently?
17. what_other_parent_misses: What does your spouse miss about your child that you see clearly?

**OUTPUT FORMAT:**
Return ONLY a valid JSON object with this structure:
{
  "parentA": {
    "easy_subject": "response",
    "growth": "response",
    ... (all 15 fields)
  },
  "parentB": {
    "easy_subject": "response",
    "growth": "response",
    ... (all 17 fields)
  }
}

Make responses:
- Authentic and specific (avoid generic statements)
- 2-4 sentences per response
- Complementary between parents but with different perspectives
- Based on the student profile data provided${contextNotes ? '\n- IMPORTANT: Incorporate the additional context/key facts provided into relevant responses where appropriate' : ''}
- Sound like real parents writing about their child`;

  try {
    const response = await fetch('/api/anthropic/mock-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'generate-parent-insights',
        prompt,
        model: 'claude-sonnet-4-20250514',
        max_tokens: 4000
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(`API request failed (${response.status}): ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();

    // Strip markdown code blocks if present (```json ... ```)
    let responseText = data.response.trim();
    if (responseText.startsWith('```')) {
      responseText = responseText.replace(/^```(?:json)?\s*\n?/, '');
      responseText = responseText.replace(/\n?```\s*$/, '');
    }

    const insights = JSON.parse(responseText);

    console.log('✅ Generated parent insights:', {
      parentAFields: Object.keys(insights.parentA).length,
      parentBFields: Object.keys(insights.parentB).length
    });

    return insights;
  } catch (error) {
    console.error('Error generating mock parent insights:', error);
    throw error;
  }
}

/**
 * Generate mock student answers to assessment questions
 * @param {array} questions - Array of question objects
 * @param {object} studentProfile - Student academic profile
 * @param {object} parentObservations - Parent insights (parentA and parentB)
 * @returns {Promise<object>} - Object with question IDs as keys and answers as values
 */
export async function generateMockAnswers(questions, studentProfile, parentObservations) {
  console.log('🤖 Generating mock answers for', questions.length, 'questions');

  const prompt = `You are helping create realistic student responses for a college assessment test profile.

**STUDENT PROFILE:**
${JSON.stringify(studentProfile, null, 2)}

**PARENT OBSERVATIONS:**
${JSON.stringify(parentObservations, null, 2)}

**ASSESSMENT QUESTIONS:**
${questions.map((q, i) => `${i + 1}. ${q.question}`).join('\n')}

**TASK:**
Generate authentic, thoughtful student responses that:
- Sound like a real high school student (not overly polished or corporate)
- Are consistent with the student profile and parent observations
- Show genuine reflection and self-awareness
- Are 3-6 sentences per answer
- Reveal personality, values, and unique perspectives
- Include specific examples and anecdotes when appropriate

**OUTPUT FORMAT:**
Return ONLY a valid JSON object with this structure:
{
  "q1": "answer to question 1",
  "q2": "answer to question 2",
  ...
}

Use keys q1, q2, q3, etc. for each question in order.`;

  try {
    const response = await fetch('/api/anthropic/mock-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'generate-student-answers',
        prompt,
        model: 'claude-sonnet-4-20250514',
        max_tokens: 3000
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(`API request failed (${response.status}): ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();

    // Strip markdown code blocks if present (```json ... ```)
    let responseText = data.response.trim();
    if (responseText.startsWith('```')) {
      responseText = responseText.replace(/^```(?:json)?\s*\n?/, '');
      responseText = responseText.replace(/\n?```\s*$/, '');
    }

    const answers = JSON.parse(responseText);

    console.log('✅ Generated mock answers:', Object.keys(answers).length);

    return answers;
  } catch (error) {
    console.error('Error generating mock answers:', error);
    throw error;
  }
}

/**
 * Generate mock follow-up answers based on previous responses
 * @param {array} questions - Array of follow-up question objects
 * @param {object} previousAnswers - Answers to questions 1-8
 * @param {object} studentProfile - Student academic profile
 * @returns {Promise<object>} - Object with question IDs as keys and answers as values
 */
export async function generateMockFollowupAnswers(questions, previousAnswers, studentProfile) {
  console.log('🤖 Generating mock follow-up answers for', questions.length, 'questions');

  const prompt = `You are helping create realistic follow-up responses for a college assessment.

**STUDENT PROFILE:**
${JSON.stringify(studentProfile, null, 2)}

**PREVIOUS ANSWERS (Questions 1-8):**
${JSON.stringify(previousAnswers, null, 2)}

**FOLLOW-UP QUESTIONS:**
${questions.map((q, i) => `${i + 9}. ${q.question}`).join('\n')}

**TASK:**
Generate authentic follow-up responses that:
- Build on and are consistent with previous answers
- Go deeper into themes already introduced
- Sound like a real student continuing a conversation
- Are 3-6 sentences per answer
- Show growth in self-awareness as the student reflects more

**OUTPUT FORMAT:**
Return ONLY a valid JSON object with this structure:
{
  "q9": "answer to question 9",
  "q10": "answer to question 10",
  ...
}

Use keys q9, q10, q11, etc. for each follow-up question in order.`;

  try {
    const response = await fetch('/api/anthropic/mock-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'generate-followup-answers',
        prompt,
        model: 'claude-sonnet-4-20250514',
        max_tokens: 3000
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      throw new Error(`API request failed (${response.status}): ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();

    // Strip markdown code blocks if present (```json ... ```)
    let responseText = data.response.trim();
    if (responseText.startsWith('```')) {
      // Remove opening ```json or ```
      responseText = responseText.replace(/^```(?:json)?\s*\n?/, '');
      // Remove closing ```
      responseText = responseText.replace(/\n?```\s*$/, '');
    }

    const answers = JSON.parse(responseText);

    console.log('✅ Generated mock follow-up answers:', Object.keys(answers).length);

    return answers;
  } catch (error) {
    console.error('Error generating mock follow-up answers:', error);
    throw error;
  }
}
